import React, { useState, useEffect, useCallback } from 'react';
import { logger } from '@/lib/logger';
import { useAppStore } from '@/stores/appStore';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { useQueryClient } from '@tanstack/react-query';
import { useMediaQuery } from '@/hooks/use-mobile';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { X, Trash2, Settings, Edit, Check, Power, ArrowLeft, ImageIcon } from 'lucide-react';
import { updateLightingControl, deleteLightingControl } from '@/services/lightingControlService';
import { batchUpdateControlScale } from '@/services/controlService';
import { useLightingEffects } from '@/contexts/LightingEffectsContext';
import { CircuitMultiSelect } from '@/components/ui/CircuitMultiSelect';
import { toast } from 'sonner';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { ProductSelectionModal } from '@/features/product-library/components';
import { useProduct } from '@/features/product-library/hooks/useProductLibrary';
import { ResizeControl } from './shared/ResizeControl';
import { IconSelector, type IconData } from '@/components/shared/IconSelector';
import { productLibraryService } from '@/features/product-library/services/productLibraryService';
import { Separator } from '@/components/ui/separator';
import { useCategoryTemplates } from '@/hooks/useCategoryTemplates';
import type { MarkerSizesConfig } from '@/types/floorPlanViewer';

export function ControlDetailsDrawerV2() {
  const isMobile = useMediaQuery('(max-width: 768px)');

  // ✅ Zustand store state
  const activeDrawer = useAppStore(state => state.ui.activeDrawer);
  const setActiveDrawer = useAppStore(state => state.setActiveDrawer);
  const selectedControls = useAppStore(state => state.selection.controls);
  const selectedProducts = useAppStore(state => state.selection.products);
  const clearEntitySelection = useAppStore(state => state.clearEntitySelection);
  const setHoveredEntity = useAppStore(state => state.setHoveredEntity);
  const setPresentationMode = useAppStore(state => state.setPresentationMode);
  const updateViewport = useAppStore(state => state.updateViewport);

  // ✅ FloorPlan context data
  const { data, floorPlan } = useFloorPlanContext();
  const queryClient = useQueryClient();

  // Get controls base scale from floor plan metadata (same as ControlLayer)
  const markerSizes = data.floorPlanMetadata?.marker_sizes as MarkerSizesConfig | null;
  const controlsBaseScale = markerSizes?.controls_base_scale ?? 1.0;

  // ✅ Local component state
  const [isUpdating, setIsUpdating] = useState(false);
  const [editingLabels, setEditingLabels] = useState<string[]>([]);
  const [tempLabels, setTempLabels] = useState<string[]>([]);
  const [actions, setActions] = useState<string[][]>([]);
  const [identifyingIndex, setIdentifyingIndex] = useState<number | null>(null);
  const [editingLabelIndex, setEditingLabelIndex] = useState<number | null>(null);
  const [labelDraft, setLabelDraft] = useState('');
  const [showProductModal, setShowProductModal] = useState(false);
  const [highlightedDropdowns, setHighlightedDropdowns] = useState<Set<number>>(new Set());
  const [isSavingIcon, setIsSavingIcon] = useState(false);
  const [isEditingType, setIsEditingType] = useState(false);
  const [editType, setEditType] = useState<string>('');
  const [allProjectCircuits, setAllProjectCircuits] = useState<any[]>([]);
  const [testModeReturn, setTestModeReturn] = useState<{
    keypadId: string;
    returnToPresentation: boolean;
    activeButtonIndices?: number[]; // Active button indices from test mode
  } | null>(null);

  // ✅ Lighting effects
  const { sim, startSim, stopSim } = useLightingEffects();

  // ✅ Computed values
  // Note: Portal-imported controls are now filtered from LVProductLayer and converted to lighting_controls
  // So we only need to handle native controls from the lighting_controls table
  const totalSelections = selectedControls.size + useAppStore.getState().selection.fixtures.size;
  const isOpen =
    activeDrawer === 'control' &&
    selectedControls.size > 0 &&
    (selectedControls.size === 1 ? totalSelections <= 1 : true);
  const selectedCount = selectedControls.size;
  const selectedControlId = selectedCount === 1 ? Array.from(selectedControls)[0] : null;
  const selectedControl = selectedControlId
    ? data.controls?.find(c => c.id === selectedControlId)
    : null;

  // Get product info if control has a product_id
  const { data: linkedProduct } = useProduct(selectedControl?.product_id || undefined);

  // Get category templates for control icons
  const { templates: controlTemplates } = useCategoryTemplates('lighting_controls');

  // Get control info
  const controlCircuit = selectedControl?.circuit_id
    ? allProjectCircuits.find(c => c.id === selectedControl.circuit_id) ||
      data.circuits?.find(c => c.id === selectedControl.circuit_id)
    : null;

  // Filter available circuits - exclude circuits assigned to other controls
  const availableCircuits =
    allProjectCircuits.length > 0
      ? allProjectCircuits.filter(circuit => {
          // Always include the circuit that's assigned to this control
          if (circuit.id === selectedControl?.circuit_id) return true;
          // Exclude circuits assigned to other controls
          return !circuit.has_control_assigned;
        })
      : data.circuits?.filter(c => {
          // Fallback to local circuits if allProjectCircuits not loaded yet
          const assignedCircuitIds =
            data.controls
              ?.filter(control => control.id !== selectedControl?.id && control.circuit_id)
              ?.map(control => control.circuit_id) || [];
          return c.id === selectedControl?.circuit_id || !assignedCircuitIds.includes(c.id);
        }) || [];

  // Control type labels
  const getControlTypeLabel = (type: string) => {
    switch (type) {
      case 'keypad':
        return 'Keypad';
      case 'dimmer':
        return 'Dimmer';
      case 'switch':
        return 'Switch';
      case 'auxiliary':
        return 'Auxiliary';
      case 'centralized-keypad':
        return 'LV Keypad';
      case 'lv-keypad':
        return 'LV Keypad';
      default:
        return type;
    }
  };

  // Clear highlights helper
  const clearHighlights = () => {
    setHighlightedDropdowns(new Set());
  };

  // Smooth animation function for viewport
  const animateViewport = useCallback(
    (target: { scale: number; positionX: number; positionY: number }, duration: number) => {
      const startTime = performance.now();
      const startViewport = useAppStore.getState().floorPlan.viewport;

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Cubic ease-in-out
        const easeProgress =
          progress < 0.5
            ? 4 * progress * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 3) / 2;

        const scale = startViewport.scale + (target.scale - startViewport.scale) * easeProgress;
        const positionX =
          startViewport.positionX + (target.positionX - startViewport.positionX) * easeProgress;
        const positionY =
          startViewport.positionY + (target.positionY - startViewport.positionY) * easeProgress;

        updateViewport({ scale, positionX, positionY });

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      // Check for reduced motion preference
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReducedMotion) {
        updateViewport(target);
      } else {
        requestAnimationFrame(animate);
      }
    },
    [updateViewport],
  );

  // Center viewport on all fixtures associated with circuit IDs (for test simulation)
  const centerViewportOnCircuits = useCallback(
    (circuitIds: string[]) => {
      if (!circuitIds || circuitIds.length === 0) return;

      // Get all fixtures for the given circuit IDs
      const circuitFixtures = data.fixtures?.filter(
        f => f.circuit_id && circuitIds.includes(f.circuit_id),
      );

      if (!circuitFixtures || circuitFixtures.length === 0) {
        logger.log('[ControlDetailsDrawer] No fixtures found for circuits:', circuitIds);
        return;
      }

      // For single fixture, just center on it
      if (circuitFixtures.length === 1) {
        const fixture = circuitFixtures[0];
        const container = document.querySelector('.floor-plan-viewport') as HTMLElement;
        if (!container) return;

        const containerRect = container.getBoundingClientRect();

        const scale = 1.5;
        const positionX = containerRect.width / 2 - fixture.x_position * scale;
        const positionY = containerRect.height / 2 - fixture.y_position * scale;

        animateViewport({ scale, positionX, positionY }, 400);
        return;
      }

      // Calculate bounding box of all fixtures
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;
      circuitFixtures.forEach(f => {
        minX = Math.min(minX, f.x_position);
        minY = Math.min(minY, f.y_position);
        maxX = Math.max(maxX, f.x_position);
        maxY = Math.max(maxY, f.y_position);
      });

      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;
      const boxWidth = maxX - minX + 300;
      const boxHeight = maxY - minY + 300;

      const container = document.querySelector('.floor-plan-viewport') as HTMLElement;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();

      const scaleX = boxWidth > 0 ? containerRect.width / boxWidth : 1;
      const scaleY = boxHeight > 0 ? containerRect.height / boxHeight : 1;
      const scale = Math.min(Math.max(Math.min(scaleX, scaleY), 0.1), 2);

      const positionX = containerRect.width / 2 - centerX * scale;
      const positionY = containerRect.height / 2 - centerY * scale;

      logger.log('[ControlDetailsDrawer] Centering viewport on circuits:', {
        circuitIds,
        fixtureCount: circuitFixtures.length,
        boundingBox: { minX, minY, maxX, maxY },
        scale,
      });

      animateViewport({ scale, positionX, positionY }, 400);
    },
    [data.fixtures, animateViewport, isMobile],
  );

  // ✅ Event handlers
  const handleClose = () => {
    setActiveDrawer(null);
    clearEntitySelection('control');
    setHoveredEntity(null, null);
    setEditingLabels([]);
    setTempLabels([]);
    setActions([]);
    setIdentifyingIndex(null);
    stopSim();
    clearHighlights();
  };

  const handleUpdateLabels = async () => {
    if (!selectedControl) return;

    try {
      setIsUpdating(true);
      await updateLightingControl(selectedControl.id, {
        labels: tempLabels.filter(label => label.trim() !== ''),
      });
      await data.refetch();
      setEditingLabels([]);
      setTempLabels([]);
      toast.success('Labels updated');
    } catch (error) {
      logger.error('Error updating labels:', error);
      toast.error('Failed to update labels');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAssignToCircuit = async (circuitId: string | null) => {
    if (!selectedControl) return;

    // Handle "none" selection to unassign circuit
    const actualCircuitId = circuitId === 'none' ? null : circuitId;

    try {
      setIsUpdating(true);
      // updateLightingControl expects circuitId (camelCase)
      await updateLightingControl(selectedControl.id, { circuitId: actualCircuitId });
      await data.refetch();
      setHoveredEntity(null, null);
      toast.success(actualCircuitId ? 'Load assigned' : 'Load removed');
    } catch (error) {
      logger.error('Error assigning circuit:', error);
      toast.error('Failed to assign load');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCloneControl = async () => {
    if (!selectedControl) return;

    try {
      setIsUpdating(true);

      // Create a clone by duplicating the control data
      const cloneData = {
        name: `${selectedControl.name} (Copy)`,
        type: selectedControl.type,
        circuitId: null, // Don't copy circuit assignment
        floorPlanId: floorPlan.id,
        numberOfButtons: selectedControl.number_of_buttons,
        labels: [...(selectedControl.labels || [])],
        actions: [...(selectedControl.actions || [])],
        xPosition: selectedControl.x + 50, // Offset position
        yPosition: selectedControl.y + 50,
        sequenceNumber: selectedControl.sequence_number,
      };

      await updateLightingControl('new', cloneData); // This might not work - simplified for now
      await data.refetch();

      toast.success('Control cloned');
    } catch (error) {
      logger.error('Error cloning control:', error);
      toast.error('Clone feature not available - please create manually');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteControl = async () => {
    if (!selectedControl) return;

    try {
      setIsUpdating(true);
      await deleteLightingControl(selectedControl.id);
      await data.refetch();
      handleClose();
      toast.success('Control deleted');
    } catch (error) {
      logger.error('Error deleting control:', error);
      toast.error('Failed to delete control');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleControlResize = async (
    ids: string[],
    scale: number,
    saveAsDefault: boolean,
    scope: 'single' | 'category' | 'subcategory' | 'type',
  ) => {
    setIsUpdating(true);
    try {
      logger.info('Control resize:', {
        ids,
        scale,
        saveAsDefault,
        scope,
        controlType: selectedControl?.type,
      });

      // Validate we have entities to resize
      if (ids.length === 0) {
        toast.error('No controls selected for resizing');
        return;
      }

      await batchUpdateControlScale(ids, scale);

      if (saveAsDefault && selectedControl) {
        // Validate control has a type before saving default
        if (!selectedControl.type) {
          logger.error('Cannot save default: control has no type');
          toast.error('Cannot save default size: control type is missing');
          return;
        }

        logger.info('Saving default scale for control type (per floor plan):', {
          type: selectedControl.type,
          floorPlanId: floorPlan.id,
        });
        // Save to floor plan metadata (per-floor-plan defaults)
        await data.saveCategoryDefault(
          'control',
          selectedControl.type,
          scale,
          undefined, // Controls don't have subcategories
        );
        logger.info('Default scale saved successfully to floor plan');
        toast.success(
          `Resized ${ids.length} control(s) and saved as default for ${selectedControl.type.replace(/_/g, ' ')} controls on this floor plan`,
        );
      } else {
        toast.success(`Resized ${ids.length} control(s)`);
      }

      await data.refetch();
    } catch (error) {
      logger.error('Failed to resize controls:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to resize controls';
      toast.error(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUnassignProduct = async () => {
    if (!selectedControl) return;
    try {
      setIsUpdating(true);
      const updateData: any = {
        productId: null,
      };
      // Clear Portal metadata if this was from Portal
      if (selectedControl.category_info?.from_portal) {
        updateData.categoryInfo = {
          ...selectedControl.category_info,
          portal_product_id: null,
          from_portal: false,
        };
      }
      await updateLightingControl(selectedControl.id, updateData);
      await data.refetch();
      toast.success('Product unassigned');
    } catch (error) {
      logger.error('Error unassigning product:', error);
      toast.error('Failed to unassign product');
    } finally {
      setIsUpdating(false);
    }
  };

  const startEditingLabels = () => {
    if (!selectedControl) return;
    const currentLabels = selectedControl.labels || [];
    setTempLabels([...currentLabels]);
    setEditingLabels(currentLabels);
  };

  const cancelEditingLabels = () => {
    setEditingLabels([]);
    setTempLabels([]);
  };

  const addLabel = () => {
    setTempLabels([...tempLabels, '']);
  };

  const removeLabel = (index: number) => {
    setTempLabels(tempLabels.filter((_, i) => i !== index));
  };

  const updateLabel = (index: number, value: string) => {
    const newLabels = [...tempLabels];
    newLabels[index] = value;
    setTempLabels(newLabels);
  };

  // Helper to trigger update safely
  const saveUpdates = async (updates: Partial<any>) => {
    if (!selectedControl) return;
    try {
      await updateLightingControl(selectedControl.id, updates);
      await data.refetch();
    } catch (err) {
      logger.error('Failed to save control updates', err);
      toast.error('Failed to save updates');
    }
  };

  // ✅ Effects
  // Prevent auto-focus on any element when drawer opens
  useEffect(() => {
    if (isOpen) {
      // Use setTimeout to ensure this runs after any auto-focus behavior
      setTimeout(() => {
        // Remove focus from any active element
        if (document.activeElement && document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }, 0);
    }
  }, [isOpen]);

  // Check if we came from test mode
  useEffect(() => {
    if (isOpen) {
      const testState = sessionStorage.getItem('testModeReturn');
      if (testState) {
        try {
          const parsed = JSON.parse(testState);
          setTestModeReturn(parsed);

          // Auto-enable test mode for the first active button if coming from test mode
          if (parsed.activeButtonIndices && parsed.activeButtonIndices.length > 0) {
            const firstActiveButton = parsed.activeButtonIndices[0];
            logger.log(
              `[ControlDetailsDrawer] Auto-enabling test mode for button ${firstActiveButton} from test drawer`,
            );
            setIdentifyingIndex(firstActiveButton);

            // Start simulation with the first active button's circuits
            setTimeout(() => {
              const buttonCircuits = actions[firstActiveButton];
              if (buttonCircuits && buttonCircuits.length > 0) {
                startSim(buttonCircuits, 'on');
              }
            }, 100); // Small delay to ensure actions are loaded
          }
        } catch (e) {
          logger.error('Failed to parse testModeReturn from sessionStorage', e);
        }
      }
    } else {
      // Clear test mode return state when drawer closes
      setTestModeReturn(null);
      setIdentifyingIndex(null);
    }
  }, [isOpen, actions, startSim]);

  // Handle ESC key to stop simulation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;

      if (sim.running) {
        stopSim();
        setIdentifyingIndex(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [sim.running, stopSim]);

  // Sync local state when control changes
  useEffect(() => {
    if (selectedControl) {
      const btns = selectedControl.number_of_buttons || 1;
      let lbls: string[] =
        selectedControl.labels && selectedControl.labels.length ? [...selectedControl.labels] : [];

      // Ensure correct length first
      while (lbls.length < btns) lbls.push('');

      // Replace empty items with defaults
      lbls = lbls.map((l, idx) => {
        const trimmed = (l || '').trim();
        return trimmed ? trimmed : `Button Label ${idx + 1}`;
      });

      setTempLabels(lbls);
      setEditingLabels([]);

      // Set actions state
      if (Array.isArray(selectedControl.actions)) {
        const processedActions = (selectedControl.actions as any[]).map(a => {
          // Handle ControlAction objects with circuitIds property
          let result: string[];
          if (typeof a === 'object' && a !== null) {
            // ControlAction object - extract circuitIds array
            if (a.circuitIds && Array.isArray(a.circuitIds)) {
              result = a.circuitIds;
            } else {
              // ControlAction exists but no circuits assigned yet
              result = [];
            }
          } else if (Array.isArray(a)) {
            // Legacy format: direct array of circuit IDs
            result = a;
          } else {
            // No value
            result = [];
          }
          return result;
        });
        setActions(processedActions);
      } else {
        // Create independent arrays for each button (not shared references!)
        setActions(Array.from({ length: btns }, () => []));
      }

      setEditingLabelIndex(null);
      setLabelDraft('');
      setIdentifyingIndex(null);
    }
  }, [
    selectedControl?.id,
    selectedControl?.numberOfButtons,
    selectedControl?.labels,
    selectedControl?.actions,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopSim();
      setHoveredEntity(null, null);
      setHighlightedDropdowns(new Set());
    };
  }, [stopSim, setHoveredEntity]);

  // Fetch all project circuits when drawer opens
  useEffect(() => {
    if (isOpen && selectedControl && floorPlan?.project_id) {
      const fetchAllProjectCircuits = async () => {
        try {
          const supabase = await getAuthenticatedSupabaseClient();
          // Get all floor plans in the project
          const { data: floorPlans, error: floorPlansError } = await supabase
            .from('floor_plans')
            .select('id, name')
            .eq('project_id', floorPlan.project_id);

          if (floorPlansError) throw floorPlansError;

          // Get all circuits from all floor plans
          if (floorPlans && floorPlans.length > 0) {
            const floorPlanIds = floorPlans.map(fp => fp.id);
            const { data: circuits, error: circuitsError } = await supabase
              .from('circuits')
              .select(
                `
                *,
                floor_plan:floor_plans!circuits_floor_plan_id_fkey(name)
              `,
              )
              .in('floor_plan_id', floorPlanIds)
              .order('sequence_number', { ascending: true });

            if (circuitsError) throw circuitsError;

            // Get all controls to check which circuits are assigned
            const { data: controls, error: controlsError } = await supabase
              .from('lighting_controls')
              .select('id, circuit_id')
              .in('floor_plan_id', floorPlanIds);

            if (controlsError) throw controlsError;

            // Map circuits with floor plan names and assignment status
            const mappedCircuits =
              circuits?.map(circuit => ({
                ...circuit,
                floor_plan_name: circuit.floor_plan?.name || 'Unknown',
                has_control_assigned: controls?.some(c => c.circuit_id === circuit.id) || false,
              })) || [];

            setAllProjectCircuits(mappedCircuits);
          }
        } catch (error) {
          logger.error('Error fetching project circuits:', error);
        }
      };

      fetchAllProjectCircuits();
    }
  }, [isOpen, selectedControl, floorPlan?.project_id]);

  // Clear highlights when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setHighlightedDropdowns(new Set());
    }
  }, [isOpen]);

  // Clear hover when drawer closes
  useEffect(() => {
    return () => {
      if (!isOpen) {
        setHoveredEntity(null, null);
      }
    };
  }, [isOpen, setHoveredEntity]);

  if (!isOpen) return null;

  // ✅ Multi-selection view
  if (selectedCount > 1) {
    return (
      <Sheet open={isOpen} modal={false}>
        <SheetContent
          className="w-[400px] sm:w-[540px] [&>button]:hidden"
          onPointerDownOutside={e => e.preventDefault()}
          onInteractOutside={e => e.preventDefault()}
        >
          <SheetHeader>
            <div className="flex items-center justify-between">
              <SheetTitle>Multiple Controls Selected</SheetTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClose}
                className="h-8 w-8"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Selection Summary */}
            <div className="text-center py-6 border rounded-lg">
              <div className="text-3xl font-bold text-purple-600 mb-2">{selectedCount}</div>
              <div className="text-gray-600">Controls Selected</div>
            </div>

            {/* Bulk Actions */}
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Bulk Actions</Label>
                <div className="grid gap-2">
                  <Button variant="outline" className="w-full" disabled={true}>
                    <Settings className="h-4 w-4 mr-2" />
                    Assign to Load (Coming Soon)
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => clearEntitySelection('control')}
                    disabled={isUpdating}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Clear Selection
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // ✅ Single control view
  if (selectedControl) {
    return (
      <>
        <Sheet open={isOpen} modal={false}>
          <SheetContent
            className="w-[400px] sm:w-[540px] flex flex-col h-full [&>button]:hidden"
            onPointerDownOutside={e => {
              e.preventDefault();
              clearHighlights();
            }}
            onInteractOutside={e => {
              e.preventDefault();
              clearHighlights();
            }}
          >
            <SheetHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {testModeReturn && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        // Clear sessionStorage
                        sessionStorage.removeItem('testModeReturn');
                        // Return to presentation drawer
                        setPresentationMode(true);
                        setActiveDrawer('presentation');
                      }}
                      className="h-8 w-8"
                      aria-label="Back to test mode"
                      title="Back to test mode"
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                  )}
                  <SheetTitle>Lighting Control</SheetTitle>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleClose}
                  className="h-8 w-8"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto py-4 space-y-6">
              {/* Control Details */}
              <div className="p-4 border rounded-lg space-y-4">
                <h4 className="text-sm font-medium">Control Details</h4>

                <div className="space-y-3">
                  {/* Type */}
                  <div className="flex justify-between items-center">
                    <Label>Type</Label>
                    {isEditingType ? (
                      <div className="flex items-center gap-2">
                        <Select value={editType} onValueChange={setEditType}>
                          <SelectTrigger className="w-[140px] h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="keypad">Keypad</SelectItem>
                            <SelectItem value="dimmer">Dimmer</SelectItem>
                            <SelectItem value="switch">Switch</SelectItem>
                            <SelectItem value="auxiliary">Auxiliary</SelectItem>
                            <SelectItem value="centralized-keypad">Centralized Keypad</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            if (editType && editType !== selectedControl.type) {
                              try {
                                setIsUpdating(true);
                                await updateLightingControl(selectedControl.id, {
                                  type: editType as any,
                                  categoryInfo: {
                                    ...selectedControl.category_info,
                                    type: editType,
                                  },
                                });
                                await data.refetch();
                                toast.success('Control type updated');
                              } catch (error) {
                                logger.error('Error updating control type:', error);
                                toast.error('Failed to update control type');
                              } finally {
                                setIsUpdating(false);
                              }
                            }
                            setIsEditingType(false);
                          }}
                          disabled={isUpdating || !editType}
                          className="h-7 w-7 p-0"
                        >
                          <Check className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setIsEditingType(false);
                            setEditType('');
                          }}
                          disabled={isUpdating}
                          className="h-7 w-7 p-0"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">
                          {getControlTypeLabel(selectedControl.type)}
                        </Badge>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditType(selectedControl.type);
                            setIsEditingType(true);
                          }}
                          disabled={isUpdating}
                          className="h-7 w-7 p-0"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Room Assignment */}
                  <div className="flex justify-between items-center">
                    <Label>Room</Label>
                    <Select
                      value={selectedControl.room_id || 'none'}
                      onValueChange={async value => {
                        const roomId = value === 'none' ? null : value;
                        try {
                          setIsUpdating(true);
                          await updateLightingControl(selectedControl.id, {
                            roomId: roomId || undefined,
                          });
                          const roomName = roomId
                            ? data.rooms?.find(r => r.id === roomId)?.name
                            : null;
                          toast.success(
                            roomName ? `Assigned to ${roomName}` : 'Room assignment removed',
                          );
                          await data.refetch();
                        } catch (error) {
                          logger.error('Error assigning room:', error);
                          toast.error('Failed to assign room');
                        } finally {
                          setIsUpdating(false);
                        }
                      }}
                      disabled={isUpdating}
                    >
                      <SelectTrigger className="w-[180px] h-8">
                        <SelectValue placeholder="Select room" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No room</SelectItem>
                        {data.rooms?.map(room => (
                          <SelectItem key={room.id} value={room.id}>
                            {room.name || 'Unnamed Room'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Number of Buttons */}
                  <div className="flex justify-between items-center">
                    <Label>Buttons</Label>
                    <Select
                      value={selectedControl.number_of_buttons?.toString() || '1'}
                      onValueChange={async value => {
                        const num = parseInt(value);

                        // Adjust labels array length to match button count
                        let updatedLabels = [...tempLabels];
                        if (updatedLabels.length < num) {
                          while (updatedLabels.length < num) {
                            updatedLabels.push(`Button ${updatedLabels.length + 1}`);
                          }
                        } else if (updatedLabels.length > num) {
                          updatedLabels = updatedLabels.slice(0, num);
                        }

                        // Adjust actions length similarly
                        let updatedActions = [...actions];
                        if (updatedActions.length < num) {
                          while (updatedActions.length < num) {
                            updatedActions.push([]);
                          }
                        } else if (updatedActions.length > num) {
                          updatedActions = updatedActions.slice(0, num);
                        }

                        setTempLabels(updatedLabels);
                        setActions(updatedActions);

                        await saveUpdates({
                          numberOfButtons: num,
                          labels: updatedLabels,
                          actions: updatedActions,
                        });
                      }}
                      disabled={isUpdating}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                          <SelectItem key={num} value={num.toString()}>
                            {num}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Sequence Number */}
                  {selectedControl.sequence_number && (
                    <div className="flex justify-between items-center">
                      <Label>Sequence</Label>
                      <Badge variant="outline">#{selectedControl.sequence_number}</Badge>
                    </div>
                  )}
                </div>
              </div>

              {/* Product Selection */}
              <div className="p-4 border rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Product Selection</Label>
                  {(() => {
                    const isPortalProduct = selectedControl.category_info?.from_portal === true;
                    const button = (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowProductModal(true)}
                        disabled={isPortalProduct || isUpdating}
                      >
                        {linkedProduct ? 'Change Product' : 'Select Product'}
                      </Button>
                    );
                    if (isPortalProduct) {
                      return (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>{button}</TooltipTrigger>
                            <TooltipContent>
                              <p>Product is linked to Portal.io proposal</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    }
                    return button;
                  })()}
                </div>

                {linkedProduct && (
                  <div className="p-4 bg-muted rounded-lg space-y-3">
                    {/* Product Image with fallbacks */}
                    {(() => {
                      const imageUrl =
                        linkedProduct?.image_url ||
                        (linkedProduct?.portal_metadata as any)?.imageUrl ||
                        (selectedControl?.category_info as any)?.portal_image_url;
                      if (!imageUrl) return null;
                      return (
                        <div className="relative aspect-square w-24 h-24 mx-auto overflow-hidden rounded-lg border bg-muted">
                          <img
                            src={imageUrl}
                            alt={`${linkedProduct?.brand || selectedControl?.name} ${linkedProduct?.model || ''}`}
                            className="h-full w-full object-contain p-1.5"
                            onError={e => {
                              const container = e.currentTarget.parentElement;
                              if (container) container.style.display = 'none';
                            }}
                          />
                        </div>
                      );
                    })()}

                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <p className="font-medium">{linkedProduct.brand}</p>
                        <p className="text-sm text-muted-foreground">{linkedProduct.model}</p>
                        {linkedProduct.part_number && (
                          <p className="text-xs font-mono text-muted-foreground">
                            {linkedProduct.part_number}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleUnassignProduct}
                        disabled={isUpdating}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    {linkedProduct.description && (
                      <p className="text-sm text-muted-foreground">{linkedProduct.description}</p>
                    )}

                    {/* Custom Icon Selector */}
                    <Separator className="my-3" />
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Product Icon</Label>
                      <IconSelector
                        currentIcon={{
                          iconType: linkedProduct.custom_icon_svg
                            ? 'svg'
                            : linkedProduct.custom_icon_abbreviation
                              ? 'abbreviation'
                              : 'none',
                          iconSvg: linkedProduct.custom_icon_svg,
                          abbreviation: linkedProduct.custom_icon_abbreviation,
                        }}
                        categoryIcon={(() => {
                          const template = controlTemplates?.find(
                            t => t.category === selectedControl?.type,
                          );
                          if (template?.icon_svg) {
                            return {
                              icon_type: 'svg' as const,
                              icon_svg: template.icon_svg,
                              fill_color: template.icon_fill_color || '#6B7280',
                            };
                          }
                          if (template?.abbreviation) {
                            return {
                              icon_type: 'abbreviation' as const,
                              abbreviation: template.abbreviation,
                              fill_color: template.icon_fill_color || '#6B7280',
                              border_color: template.icon_border_color || '#374151',
                              text_color: template.icon_text_color || '#FFFFFF',
                            };
                          }
                          return {
                            icon_type: 'abbreviation' as const,
                            abbreviation: (linkedProduct.model || linkedProduct.brand || 'CTL')
                              .slice(0, 3)
                              .toUpperCase(),
                            fill_color: '#6B7280',
                            border_color: '#374151',
                            text_color: '#FFFFFF',
                          };
                        })()}
                        productName={`${linkedProduct.brand} ${linkedProduct.model}`}
                        onSave={async (iconData: IconData) => {
                          setIsSavingIcon(true);
                          try {
                            await productLibraryService.updateCustomIcon(linkedProduct.id, {
                              custom_icon_svg:
                                iconData.iconType === 'svg' ? iconData.iconSvg : null,
                              custom_icon_abbreviation:
                                iconData.iconType === 'abbreviation' ? iconData.abbreviation : null,
                            });
                            // Invalidate product queries to refresh data
                            queryClient.invalidateQueries({
                              queryKey: ['product', linkedProduct.id],
                            });
                            queryClient.invalidateQueries({ queryKey: ['product-library'] });
                          } finally {
                            setIsSavingIcon(false);
                          }
                        }}
                        isSaving={isSavingIcon}
                        previewSize={24}
                      />
                      <p className="text-xs text-muted-foreground">
                        Custom icons apply to all placements of this product model.
                      </p>
                    </div>
                  </div>
                )}

                {!linkedProduct && (
                  <div className="p-2 bg-muted/50 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">
                      No product selected. This is a generic{' '}
                      {selectedControl.type.replace(/-/g, ' ')}.
                    </p>
                  </div>
                )}

                {/* Show Product Image Toggle - only show if product has image */}
                {linkedProduct &&
                  (() => {
                    const imageUrl =
                      linkedProduct?.image_url ||
                      (linkedProduct?.portal_metadata as any)?.imageUrl ||
                      (selectedControl?.category_info as any)?.portal_image_url;
                    if (!imageUrl) return null;
                    return (
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-2">
                          <ImageIcon className="h-4 w-4 text-muted-foreground" />
                          <Label
                            htmlFor="show-product-image"
                            className="cursor-pointer text-sm font-normal"
                          >
                            Show image on floor plan
                          </Label>
                        </div>
                        <Switch
                          id="show-product-image"
                          checked={selectedControl.show_product_image || false}
                          onCheckedChange={async checked => {
                            try {
                              setIsUpdating(true);
                              await updateLightingControl(selectedControl.id, {
                                showProductImage: checked,
                              });
                              await data.refetch();
                              toast.success(
                                checked
                                  ? 'Product image shown on floor plan'
                                  : 'Product image hidden',
                              );
                            } catch (error) {
                              logger.error('Error updating show product image:', error);
                              toast.error('Failed to update setting');
                            } finally {
                              setIsUpdating(false);
                            }
                          }}
                          disabled={isUpdating}
                        />
                      </div>
                    );
                  })()}
              </div>

              {/* Load Assignment */}
              <div className="p-4 border rounded-lg space-y-4">
                <h4 className="text-sm font-medium">Load Assignment</h4>

                {/* Current Circuit */}
                {controlCircuit ? (
                  <div className="space-y-3">
                    <div>
                      <Label>Current Load</Label>
                      <div className="mt-2 flex items-center justify-between">
                        <Badge>
                          {controlCircuit.sequence_number
                            ? `${controlCircuit.sequence_number} - `
                            : ''}
                          {controlCircuit.name}
                        </Badge>
                      </div>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAssignToCircuit(null)}
                      disabled={isUpdating}
                      className="w-full"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Remove from Load
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500">Not assigned to any load</div>
                )}

                {/* Circuit Selection */}
                <div>
                  <Label>Assign to Load</Label>
                  <Select onValueChange={handleAssignToCircuit} disabled={isUpdating}>
                    <SelectTrigger className="mt-2">
                      <SelectValue
                        placeholder={
                          availableCircuits.length > 0 ? 'Select a load' : 'No loads available'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {/* None option */}
                      <SelectItem value="none">None</SelectItem>

                      {/* Currently Assigned section */}
                      {controlCircuit && (
                        <div>
                          <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground bg-muted/50">
                            Currently Assigned
                          </div>
                          <SelectItem
                            key={controlCircuit.id}
                            value={controlCircuit.id}
                            onMouseEnter={() => setHoveredEntity(controlCircuit.id, 'circuit')}
                            onMouseLeave={() => setHoveredEntity(null, null)}
                          >
                            {controlCircuit.sequence_number
                              ? `${controlCircuit.sequence_number} - `
                              : ''}
                            {controlCircuit.name}
                            {controlCircuit.floor_plan_name &&
                            controlCircuit.floor_plan_id !== floorPlan?.id
                              ? ` (${controlCircuit.floor_plan_name})`
                              : ''}
                          </SelectItem>
                        </div>
                      )}

                      {/* Group circuits by floor plan */}
                      {(() => {
                        // Group available circuits by floor plan
                        const circuitsByFloorPlan = availableCircuits.reduce(
                          (acc, circuit) => {
                            // Skip the currently assigned circuit as it's shown above
                            if (circuit.id === selectedControl?.circuit_id) return acc;

                            const floorPlanName = circuit.floor_plan_name || 'Unknown';
                            if (!acc[floorPlanName]) {
                              acc[floorPlanName] = [];
                            }
                            acc[floorPlanName].push(circuit);
                            return acc;
                          },
                          {} as Record<string, typeof availableCircuits>,
                        );

                        // Get current floor plan name
                        const currentFloorPlanName = Object.keys(circuitsByFloorPlan).find(name =>
                          circuitsByFloorPlan[name].some(c => c.floor_plan_id === floorPlan?.id),
                        );

                        // Separate current floor plan from others
                        const otherFloorPlanNames = Object.keys(circuitsByFloorPlan)
                          .filter(name => name !== currentFloorPlanName)
                          .sort();

                        return (
                          <>
                            {/* Current Floor Plan section */}
                            {currentFloorPlanName &&
                              circuitsByFloorPlan[currentFloorPlanName]?.length > 0 && (
                                <div>
                                  <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground bg-muted/50">
                                    Current Floor Plan
                                  </div>
                                  {circuitsByFloorPlan[currentFloorPlanName].map(circuit => (
                                    <SelectItem
                                      key={circuit.id}
                                      value={circuit.id}
                                      onMouseEnter={() => setHoveredEntity(circuit.id, 'circuit')}
                                      onMouseLeave={() => setHoveredEntity(null, null)}
                                    >
                                      {circuit.sequence_number
                                        ? `${circuit.sequence_number} - `
                                        : ''}
                                      {circuit.name}
                                    </SelectItem>
                                  ))}
                                </div>
                              )}

                            {/* Other Floor Plans sections */}
                            {otherFloorPlanNames.map(floorPlanName => (
                              <div key={floorPlanName}>
                                <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground bg-muted/50">
                                  {floorPlanName}
                                </div>
                                {circuitsByFloorPlan[floorPlanName].map(circuit => (
                                  <SelectItem
                                    key={circuit.id}
                                    value={circuit.id}
                                    onMouseEnter={() => setHoveredEntity(circuit.id, 'circuit')}
                                    onMouseLeave={() => setHoveredEntity(null, null)}
                                  >
                                    {circuit.sequence_number ? `${circuit.sequence_number} - ` : ''}
                                    {circuit.name}
                                  </SelectItem>
                                ))}
                              </div>
                            ))}

                            {/* No circuits available message */}
                            {Object.keys(circuitsByFloorPlan).length === 0 && !controlCircuit && (
                              <SelectItem value="no-circuits" disabled>
                                All loads are already assigned
                              </SelectItem>
                            )}
                          </>
                        );
                      })()}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Labels Section - Only show if control has buttons */}
              {selectedControl.number_of_buttons > 0 && (
                <div className="p-4 border rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium">Button Labels and Actions</h4>
                  </div>

                  <div className="space-y-3">
                    {Array.from({ length: selectedControl.number_of_buttons }).map((_, index) => (
                      <div key={index} className="grid gap-2">
                        <div className="flex items-center gap-2">
                          <div className="bg-muted text-muted-foreground w-6 h-6 rounded-full flex items-center justify-center font-medium text-xs shrink-0">
                            {index + 1}
                          </div>
                          <div className="flex items-center justify-between gap-2 flex-1 max-w-[calc(100%-2px)]">
                            {editingLabelIndex === index ? (
                              <Input
                                value={labelDraft}
                                onChange={e => setLabelDraft(e.target.value)}
                                className="h-8 flex-1"
                                autoFocus={false}
                                onKeyDown={async e => {
                                  e.stopPropagation(); // Prevent space-to-pan interference
                                  if (e.key === 'Enter') {
                                    const trimmed = labelDraft.trim();
                                    const newLabels = [...tempLabels];
                                    newLabels[index] = trimmed;
                                    setTempLabels(newLabels);
                                    await saveUpdates({ labels: newLabels });
                                    setEditingLabelIndex(null);
                                  }
                                }}
                                onKeyUp={e => e.stopPropagation()}
                              />
                            ) : (
                              <span
                                className="truncate flex-1 cursor-text"
                                title={tempLabels[index] || ''}
                                onClick={() => clearHighlights()}
                              >
                                {tempLabels[index] || '-'}
                              </span>
                            )}

                            {/* Right side buttons */}
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0 text-muted-foreground hover:text-primary"
                                onClick={() => {
                                  if (editingLabelIndex === index) {
                                    const trimmed = labelDraft.trim();
                                    const newLabels = [...tempLabels];
                                    newLabels[index] = trimmed;
                                    setTempLabels(newLabels);
                                    saveUpdates({ labels: newLabels });
                                    setEditingLabelIndex(null);
                                  } else {
                                    clearHighlights();
                                    setLabelDraft(tempLabels[index] || '');
                                    setEditingLabelIndex(index);
                                  }
                                }}
                              >
                                {editingLabelIndex === index ? (
                                  <Check className="h-4 w-4" />
                                ) : (
                                  <Edit className="h-4 w-4" />
                                )}
                              </Button>
                              <Button
                                variant={
                                  sim.running && identifyingIndex === index ? 'default' : 'outline'
                                }
                                size="sm"
                                className="h-8 w-8 p-0"
                                disabled={actions[index]?.length === 0}
                                onClick={() => {
                                  clearHighlights();
                                  const ids = actions[index] || [];
                                  if (ids.length === 0) {
                                    // Highlight the dropdown instead of starting simulation
                                    setHighlightedDropdowns(new Set([index]));
                                    return;
                                  }

                                  // Check if this button is currently active
                                  const isCurrentlyActive =
                                    sim.running && identifyingIndex === index;

                                  if (isCurrentlyActive) {
                                    // Deactivate - stop simulation
                                    stopSim();
                                    setIdentifyingIndex(null);
                                  } else {
                                    // Activate - stop any existing simulation first, then start new one
                                    stopSim();
                                    startSim(ids, 'on');
                                    setIdentifyingIndex(index);
                                    // Center viewport to show all fixtures in this simulation
                                    centerViewportOnCircuits(ids);
                                  }
                                }}
                              >
                                <Power className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Circuit Assignment for this button */}
                        <div className="ml-8 max-w-[calc(100%-34px)]">
                          <CircuitMultiSelect
                            key={`circuit-select-${selectedControl.id}-${index}`}
                            selected={actions[index] || []}
                            onSelectionChange={ids => {
                              // Real-time lighting effects - ONLY update when this button is in test mode
                              if (identifyingIndex === index) {
                                if (ids.length > 0) {
                                  startSim(ids, 'on');
                                } else {
                                  stopSim();
                                }
                              }
                            }}
                            onChange={async ids => {
                              clearHighlights();
                              const newActions = [...actions];
                              newActions[index] = ids;
                              setActions(newActions);

                              // Reconstruct ControlAction objects for saving
                              const controlActions = (selectedControl.actions as any[]).map(
                                (action, i) => ({
                                  ...action,
                                  circuitIds: newActions[i] || [],
                                }),
                              );

                              // Save immediately
                              await saveUpdates({ actions: controlActions });
                            }}
                            options={(() => {
                              // Use all project circuits if available, otherwise fall back to local circuits
                              const circuits =
                                allProjectCircuits.length > 0 ? allProjectCircuits : data.circuits;
                              return (
                                circuits?.map(c => ({
                                  id: c.id,
                                  label: `${c.sequence_number ? `${c.sequence_number} - ` : ''}${c.name}`,
                                  floor_plan_id: c.floor_plan_id,
                                  floor_plan_name: c.floor_plan_name || floorPlan?.name,
                                })) || []
                              );
                            })()}
                            currentFloorPlanId={floorPlan?.id}
                            className={`w-full ${highlightedDropdowns.has(index) ? 'ring-2 ring-red-500 ring-offset-2' : ''}`}
                            placeholder="Select loads for this button"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Resize Control */}
              <div className="p-4 border rounded-lg space-y-4">
                <h4 className="text-sm font-medium">Scale</h4>
                <ResizeControl
                  entityType="control"
                  entity={selectedControl}
                  allEntities={data.controls || []}
                  onResize={handleControlResize}
                  isUpdating={isUpdating}
                  baseScale={controlsBaseScale}
                />
              </div>

              {/* Actions */}
              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="text-sm font-medium">Actions</h4>

                <div className="grid gap-2">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" disabled={isUpdating} className="w-full">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Control
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Control</AlertDialogTitle>
                        <AlertDialogDescription>
                          This action cannot be undone. This will permanently delete this{' '}
                          {getControlTypeLabel(selectedControl.type).toLowerCase()} control
                          {controlCircuit && ` from load "${controlCircuit.name}"`}.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDeleteControl}
                          className="bg-destructive hover:bg-destructive/90"
                        >
                          Delete Control
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Product Selection Modal */}
        {selectedControl && (
          <ProductSelectionModal
            open={showProductModal}
            onOpenChange={setShowProductModal}
            currentProductId={selectedControl.product_id}
            category="control"
            subcategory={selectedControl.type}
            title="Select Control Product"
            onSelect={async product => {
              try {
                setIsUpdating(true);

                // Update control with product_id
                await updateLightingControl(selectedControl.id, {
                  productId: product?.id || null,
                });

                await data.refetch();

                toast.success(
                  product ? `Linked to ${product.brand} ${product.model}` : 'Product link removed',
                );

                setShowProductModal(false);
              } catch (error) {
                logger.error('Error updating product link:', error);
                toast.error('Failed to update product link');
              } finally {
                setIsUpdating(false);
              }
            }}
          />
        )}
      </>
    );
  }

  return null;
}
